import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import "./home.css";
import "./crm.css";
import api from "../lib/api";
import { ReactComponent as TidaLogo } from '../tida.svg';

// ONE package of icons
import { GiCutDiamond, GiDiamondRing, GiLotus, GiWrappedSweet } from 'react-icons/gi';
import { FaLaptopCode, FaUtensils, FaRegStar, FaCode } from 'react-icons/fa';

// Wrap TidaLogo so it accepts the same { style } prop as react-icons components
const TidaIcon = ({ style }) => <TidaLogo style={style} />;

const ICON_SET = [
  { Comp: GiCutDiamond,    color: 'rgba(255,255,255,0.98)' },
  { Comp: GiDiamondRing,   color: 'rgba(255,255,255,0.98)' },
  { Comp: TidaIcon,        color: '#FFD200' },                // tida logo gold
  { Comp: FaUtensils,      color: 'rgba(255,255,255,0.98)' },
  { Comp: GiWrappedSweet,  color: '#FFD166' },                // mithai
  { Comp: FaLaptopCode,    color: 'rgba(255,255,255,0.98)' },
  { Comp: FaCode,          color: 'rgba(255,255,255,0.98)' },
  { Comp: GiLotus,         color: 'rgba(255,255,255,0.98)' },
  { Comp: FaRegStar,       color: 'rgba(255,255,255,0.98)' },
];

const MAX_FLOATERS = 108;
const INITIAL_COUNT = 24;   // big to start, but not too crazy
const ADD_COUNT = 6;        // how many to spawn per click

const Home = () => {
  const [topic, setTopic] = useState('');
  const [status, setStatus] = useState(null); // 'sent' | 'error' | null
  const [cooldown, setCooldown] = useState(0); // seconds remaining
  const [chauvihaar, setChauvihaar] = useState(new Date().toLocaleTimeString());
  const [floaters, setFloaters] = useState([]);
  const idRef = useRef(0);
  const clickLayerRef = useRef(null);


  // Initial icons
  useEffect(() => {
    setFloaters(genIcons(INITIAL_COUNT));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate N icons (optionally around a given x in %)
  function genIcons(n, xPercent = null) {
    const arr = [];
    for (let i = 0; i < n; i++) {
      const base = ICON_SET[(idRef.current + i) % ICON_SET.length];
      const jitter = (Math.random() * 16) - 8; // -8%..+8%
      const left = xPercent == null
        ? `${Math.random() * 100}%`
        : `${Math.min(96, Math.max(4, xPercent + jitter))}%`;

      const size = 36 + Math.round(Math.random() * 26);    // BIGGER (36–62px)
      const duration = 16 + Math.random() * 14;            // 16–30s
      const delay = (Math.random() * -20).toFixed(2);      // negative to spread starts
      const drift = ((Math.random() * 2 - 1) * 28).toFixed(1); // sway amplitude px

      arr.push({
        id: idRef.current++,
        Comp: base.Comp,
        color: base.color,
        left,
        size,
        duration,
        delay,
        drift
      });
    }
    return arr;
  }

  // Click background to add icons (clusters near click x)
  const handleBackgroundClick = (e) => {
    const el = clickLayerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100;

    setFloaters(prev => {
      const next = [...prev, ...genIcons(ADD_COUNT, xPercent)];
      // cap to MAX_FLOATERS (drop oldest)
      return next.length > MAX_FLOATERS ? next.slice(next.length - MAX_FLOATERS) : next;
    });
  };

  const fetchSunsetTime = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const { latitude, longitude } = coords;
        api
          .get(`https://api.sunrise-sunset.org/json?lat=${latitude}&lng=${longitude}&date=today&formatted=0`)
          .then((response) => {
            const sunset = new Date(response.data.results.sunset);
            sunset.setMinutes(sunset.getMinutes() - 48);
            setChauvihaar(sunset.toLocaleTimeString());
          })
          .catch(() => {});
      },
      () => {}
    );
  };

  const cooldownRef = useRef(null);

  useEffect(() => () => clearInterval(cooldownRef.current), []);

  const submitNote = async () => {
    if (!topic.trim() || cooldown > 0) return;
    setStatus(null);
    try {
      await api.post("/admin/notes/adit/guest", {
        topic: topic.trim(),
        date: new Date()
      });
      setTopic('');
      setStatus('sent');
      setTimeout(() => setStatus(null), 2500);
      setCooldown(30);
      clearInterval(cooldownRef.current);
      cooldownRef.current = setInterval(() => {
        setCooldown(c => {
          if (c <= 1) { clearInterval(cooldownRef.current); return 0; }
          return c - 1;
        });
      }, 1000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus(null), 3000);
    }
  };

  return (
    <div className="home-root">
      <Helmet><title>Adit Mehta</title></Helmet>
      {/* Animated gradient background */}
      <div className="bg-anim" aria-hidden="true" />

      {/* Click catcher (behind content, above bg) */}
      <div
        ref={clickLayerRef}
        className="click-catcher"
        onClick={handleBackgroundClick}
        aria-hidden="true"
      />

      {/* Floating brand icons (no pointer events so UI stays clickable) */}
      <div className="icon-field" aria-hidden="true">
        {floaters.map(({ id, Comp, color, left, size, duration, delay, drift }) => (
          <div
            key={id}
            className="floater"
            style={{
              left,
              '--drift': `${drift}px`,
              animation: `rise ${duration}s linear infinite`,
              animationDelay: `${delay}s`,
            }}
          >
            <Comp style={{ color, width: size, height: size, filter: 'drop-shadow(0 3px 8px rgba(0,0,0,0.3))' }} />
          </div>
        ))}
      </div>

      {/* Content (above everything) */}
      <div className="container-fluid px-0 home-content">
        {/* top right admin & time */}
        <div className="home-topbar">
          <button
            onClick={fetchSunsetTime}
            className="home-sunset-btn"
            title="Click to update to today's (adjusted) sunset time"
          >
            {chauvihaar}
          </button>
        </div>

        {/* hero */}
        <header className="home-header">
          <h1 className="title title-margin">
            <span className="title-muted">Welcome to</span>{' '}
            <span className="span2 title-strong"><TidaLogo className="title-tida" aria-hidden="true" />dit Mehta</span>{' '}
            <span className="title-muted">(dot) com</span>
          </h1>
        </header>

       {/* Centered block */}
<div className="hero-center">
  <p className="subtitle">Choose your journey</p>

  <div className="choice-row">
    <div className="choice-dropdown">
      <span className="choice-btn choice-btn--parent">Professional</span>
      <div className="choice-subnav">
        <a className="choice-sublink" href="/professional">
          <span className="choice-sublink-icon">💼</span>
          <span className="choice-sublink-text">
            <span className="choice-sublink-name">Resume</span>
            <span className="choice-sublink-desc">Work, skills & background</span>
          </span>
        </a>
        <a className="choice-sublink" href="/mentor">
          <span className="choice-sublink-icon">🤝</span>
          <span className="choice-sublink-text">
            <span className="choice-sublink-name">Mentorship</span>
            <span className="choice-sublink-desc">Guidance & community</span>
          </span>
        </a>
        <a className="choice-sublink" href="/project-playground">
          <span className="choice-sublink-icon">🧪</span>
          <span className="choice-sublink-text">
            <span className="choice-sublink-name">Project Playground</span>
            <span className="choice-sublink-desc">Live demos & experiments</span>
          </span>
        </a>
      </div>
    </div>

    <div className="choice-dropdown">
      <span className="choice-btn choice-btn--parent">Creative</span>
      <div className="choice-subnav">
        <a className="choice-sublink" href="/experiences">
          <span className="choice-sublink-icon">✨</span>
          <span className="choice-sublink-text">
            <span className="choice-sublink-name">Experiences</span>
            <span className="choice-sublink-desc">Curated moments, made for you</span>
          </span>
        </a>
        <a className="choice-sublink" href="/provoke">
          <span className="choice-sublink-icon">🔥</span>
          <span className="choice-sublink-text">
            <span className="choice-sublink-name">Provoke</span>
            <span className="choice-sublink-desc">AI that challenges your thinking</span>
          </span>
        </a>
        <a className="choice-sublink" href="/signal">
          <span className="choice-sublink-icon">🌙</span>
          <span className="choice-sublink-text">
            <span className="choice-sublink-name">Signal Room</span>
            <span className="choice-sublink-desc">Midnight letters & dispatches</span>
          </span>
        </a>
        <a className="choice-sublink" href="/recipies">
          <span className="choice-sublink-icon">🍴</span>
          <span className="choice-sublink-text">
            <span className="choice-sublink-name">Recipes</span>
            <span className="choice-sublink-desc">Food I actually cook</span>
          </span>
        </a>
        <a className="choice-sublink" href="/blog">
          <span className="choice-sublink-icon">📝</span>
          <span className="choice-sublink-text">
            <span className="choice-sublink-name">Blog</span>
            <span className="choice-sublink-desc">Writing & longer thoughts</span>
          </span>
        </a>
        <a className="choice-sublink" href="/jainism">
          <span className="choice-sublink-icon">🕊️</span>
          <span className="choice-sublink-text">
            <span className="choice-sublink-name">Jainism & Me</span>
            <span className="choice-sublink-desc">Resources, community, and what it means to me</span>
          </span>
        </a>
      </div>
    </div>
  </div>
</div>
        {/* Bottom readability gradient */}
        <div aria-hidden="true" className="home-bottom-gradient" />

        {/* note sender */}
        <footer
          className="sender sender-layout"
        >
          <input
            className="newnote sendinput sendinput-wide"
            type="text"
            placeholder="Send Adit a Note!"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitNote(); }}
            aria-label="Send Adit a Note"
          />
          <input
            type="submit"
            value={cooldown > 0 ? `${cooldown}s` : status === 'sent' ? 'Sent ✓' : status === 'error' ? 'Retry' : 'Send'}
            className="bt2 sendbt"
            onClick={submitNote}
            disabled={!topic.trim() || cooldown > 0}
            aria-disabled={!topic.trim() || cooldown > 0}
          />
        </footer>
      </div>
    </div>
  );
};

export default Home;
